import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MessageCircle, Clock, Tag, ArchiveX } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr, fmtDate, imgUrl, L, pickLang } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { useAuth } from "@/context/AuthContext";
import { LangToggle } from "@/components/news/SiteHeader";
import { TrendBadge } from "@/components/news/TrendBadge";
import { useBrand } from "@/components/news/Brand";
import { StoryCard } from "@/components/news/StoryCard";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const slugId = (t, i) => `sec-${i + 1}`;

const prepare = (html) => {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const toc = [];
  doc.body.querySelectorAll("h2, h3").forEach((h, i) => {
    h.id = slugId(h.textContent, i);
    if (h.tagName === "H2") toc.push({ id: h.id, text: h.textContent.trim() });
  });
  return { html: doc.body.innerHTML, toc };
};

const Toc = ({ items, lang }) => (
  <nav data-testid="story-toc" className="mb-10 border-l-4 border-[#1D4ED8] bg-blue-50/60 px-5 py-4">
    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#0B2A5B] mb-2">{lang === "ta" ? "இந்தச் செய்தியில்" : "In this story"}</p>
    <ol className="space-y-1.5 text-sm list-decimal pl-5">
      {items.map((t) => (
        <li key={t.id}><a href={`#${t.id}`} className="text-[#0B2A5B] hover:text-[#1D4ED8] hover:underline">{t.text}</a></li>
      ))}
    </ol>
  </nav>
);

const Body = ({ content, images, lang }) => {
  const { html, toc } = useMemo(() => prepare(content), [content]);
  return (
    <>
      {toc.length >= 3 && <Toc items={toc} lang={lang} />}
      <div data-testid="story-body" className="article-body text-base sm:text-lg" dangerouslySetInnerHTML={{ __html: html }} />
      {images.map((u, j) => (
        <figure key={u} className="my-10"><img src={imgUrl(u)} alt="" data-testid={`inline-image-${j}`} className="w-full max-h-[70vh] object-cover" /></figure>
      ))}
    </>
  );
};

const Comments = ({ slug, lang }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/public/stories/${slug}/comments`).then((r) => setItems(r.data)).catch(() => {});
  }, [slug]);

  const post = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/public/stories/${slug}/comments`, { text });
      setItems([data, ...items]);
      setText("");
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="comments-section" className="mt-16 border-t-2 border-zinc-900 pt-8">
      <h2 className="font-serif-head text-2xl font-black mb-6">{L("comments", lang)} <span className="text-zinc-400">({items.length})</span></h2>
      {user ? (
        <form onSubmit={post} className="mb-10">
          <Textarea data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={1000}
            placeholder="Share your view. Keep it respectful." className="rounded-none border-zinc-300 min-h-[96px]" />
          <div className="mt-3 flex items-center justify-between">
            <span className="text-xs text-zinc-500">Commenting as {user.name}</span>
            <Button data-testid="comment-submit-btn" disabled={busy || !text.trim()} className="rounded-none">Post comment</Button>
          </div>
        </form>
      ) : (
        <div data-testid="comment-login-prompt" className="mb-10 border border-zinc-300 bg-white p-5 text-sm">
          <Link to="/login" className="font-bold underline">Sign in</Link> or{" "}
          <Link to="/register" className="font-bold underline">create a free reader account</Link> to comment.
        </div>
      )}
      <ul className="divide-y divide-zinc-200">
        {items.map((c) => (
          <li key={c.id} data-testid={`comment-${c.id}`} className="py-5">
            <p className="text-sm font-bold">{c.user_name} <span className="ml-2 font-normal text-xs text-zinc-500 font-mono-meta">{fmtDate(c.created_at)}</span></p>
            <p className="mt-1 text-zinc-700 whitespace-pre-line">{c.text}</p>
          </li>
        ))}
      </ul>
    </section>
  );
};

const Withdrawn = ({ story, lang }) => {
  const brand = useBrand();
  return (
  <div data-testid="withdrawn-notice" className="max-w-3xl mx-auto px-4 sm:px-6 py-24 fade-in">
    <ArchiveX className="h-10 w-10 text-zinc-500" />
    <p className="mt-6 text-[11px] font-bold uppercase tracking-[0.16em] text-[#1D4ED8]">{brand.name} · {brand.tagline}</p>
    <h1 className="mt-2 font-serif-head font-black text-4xl sm:text-5xl tracking-tight">This story has been withdrawn</h1>
    <p className={`mt-6 text-lg text-zinc-600 ${pickLang(story, lang) === "ta" ? "lang-ta" : ""}`}>
      “{story[pickLang(story, lang)]?.headline}”
    </p>
    <p className="mt-4 text-zinc-600">Our editors have withdrawn this article{story.withdrawn_at ? ` on ${fmtDate(story.withdrawn_at)}` : ""}. It is no longer available.</p>
    <Link to="/" data-testid="withdrawn-home-link" className="inline-block mt-10 bg-zinc-900 text-white px-5 py-3 text-sm font-bold">Back to latest news</Link>
  </div>
  );
};

export default function StoryPage() {
  const { slug } = useParams();
  const { lang } = useSite();
  const [story, setStory] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setStory(null);
    setMissing(false);
    window.scrollTo(0, 0);
    api.get(`/public/stories/${slug}`).then((r) => setStory(r.data)).catch(() => setMissing(true));
  }, [slug]);

  if (missing)
    return (
      <div data-testid="story-not-found" className="max-w-3xl mx-auto px-4 py-24">
        <h1 className="font-serif-head font-black text-4xl">Story not found</h1>
        <Link to="/" className="underline mt-6 inline-block">Go to homepage</Link>
      </div>
    );
  if (!story) return <div className="max-w-3xl mx-auto px-4 py-24 text-zinc-400">Loading…</div>;
  if (story.withdrawn) return <Withdrawn story={story} lang={lang} />;

  const vl = pickLang(story, lang);
  const v = story[vl];
  const both = (story.languages || []).length > 1;
  const longHead = (v.headline || "").length > 80;
  const cat = story.category ? (lang === "ta" ? story.category.name_ta : story.category.name_en) : "";
  const shareUrl = `${window.location.origin}/story/${story.slug}`;
  const wa = `https://wa.me/?text=${encodeURIComponent(`${v.headline}\n${shareUrl}`)}`;

  return (
    <article data-testid="story-page" className="fade-in">
      <div className={`max-w-3xl mx-auto px-4 sm:px-6 pt-10 ${vl === "ta" ? "lang-ta" : ""}`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <TrendBadge trend={story.trend_type} lang={lang} testId="story-trend-badge" />
            {story.category && (
              <Link to={`/category/${story.category.slug}`} data-testid="story-category" className="text-xs font-bold uppercase tracking-[0.14em] text-zinc-600 hover:text-zinc-950">{cat}</Link>
            )}
          </div>
          {both ? <LangToggle testId="story-lang-switcher" /> : (
            <span data-testid="story-single-lang" className="text-[11px] font-semibold uppercase tracking-[0.1em] text-zinc-500 border border-zinc-300 px-2 py-1">
              {vl === "ta" ? "தமிழில் மட்டும்" : "English only"}
            </span>
          )}
        </div>
        <h1 data-testid="story-headline" className={`mt-6 font-serif-head font-black tracking-tight leading-[1.2] text-zinc-950 ${longHead ? "text-2xl sm:text-3xl lg:text-4xl" : "text-4xl sm:text-5xl lg:text-6xl"}`}>
          {v.headline}
        </h1>
        {v.summary && <p data-testid="story-summary" className="mt-6 text-lg sm:text-xl text-zinc-600 leading-relaxed">{v.summary}</p>}
        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-y border-zinc-300 py-4">
          <div className="text-sm">
            <p className="text-xs text-zinc-500 font-mono-meta flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(story.published_at, lang)}</p>
          </div>
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-share-btn"
            className="inline-flex items-center gap-2 bg-[#128C4A] hover:bg-[#0f7a40] text-white px-4 py-2.5 text-sm font-bold transition-colors">
            <MessageCircle className="h-4 w-4" /> {L("share", lang)}
          </a>
        </div>
      </div>
      {story.cover_image && (
        <div className="max-w-5xl mx-auto sm:px-6 mt-10">
          <img data-testid="story-cover-image" src={imgUrl(story.cover_image)} alt="" className="w-full max-h-[80vh] object-cover" />
        </div>
      )}
      <div className={`max-w-3xl mx-auto px-4 sm:px-6 mt-10 ${vl === "ta" ? "lang-ta" : ""}`}>
        <Body content={v.content} images={story.images || []} lang={vl} />
        {story.tags?.length > 0 && (
          <div data-testid="story-tags" className="mt-10 flex flex-wrap items-center gap-2">
            <Tag className="h-4 w-4 text-zinc-500" />
            {story.tags.map((t) => <span key={t} className="text-xs font-mono-meta border border-zinc-300 px-2 py-1 bg-white">{t}</span>)}
          </div>
        )}
        <Comments slug={story.slug} lang={lang} />
      </div>
      {story.related?.length > 0 && (
        <section data-testid="related-stories" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20">
          <h2 className="font-serif-head text-2xl font-black border-b-2 border-zinc-900 pb-2 mb-8">{L("related", lang)}</h2>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {story.related.map((s) => <StoryCard key={s.id} story={s} lang={lang} />)}
          </div>
        </section>
      )}
    </article>
  );
}
